import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { COMPANY, HERO, DECI_FEATURES, PEOPLE, PROJECTS } from "@/content";

const fade = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.45 } };

export default function Home() {
  const [news, setNews] = useState([]);
  useEffect(() => { api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="hero">
        <div className="wrap hero-inner">
          <div>
            <span className="hero-label">{HERO.label}</span>
            <h1>{HERO.heading}</h1>
            <p>{HERO.sub}</p>
            <ul className="hero-points">
              {HERO.points.map((p) => (<li key={p}>{p}</li>))}
            </ul>
            <div className="hero-actions">
              <Link to="/deci" className="btn btn-primary" data-testid="hero-deci-btn">Explore DECI</Link>
              <Link to="/investor-relations" className="btn btn-ghost" data-testid="hero-investor-btn">Investor Relations</Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="brain-frame">
              <img src="/brand/brain-1.jpg" alt="DECI real-time 3D brain imaging" />
              <span className="brain-tag">DECI cortical imaging</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section">
        <div className="wrap">
          <span className="eyebrow">Technology</span>
          <h2 className="h-sec" style={{ color: "#fff" }}>What DECI does</h2>
          <p className="lead" style={{ color: "#fff" }}>DECI makes the brain's electrical activity visible as moving 3D images, built on ordinary EEG hardware.</p>
          <div className="feature-grid">
            {DECI_FEATURES.map((f, i) => (
              <motion.div className="feature-card" key={f.title} {...fade} transition={{ duration: 0.4, delay: i * 0.06 }} data-testid={`feature-${i}`}>
                <span className="feature-num">{String(i + 1).padStart(2, "0")}</span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section soft">
        <div className="wrap split">
          <div>
            <span className="eyebrow">About the company</span>
            <h2 className="h-sec">Affordable, objective brain diagnostics</h2>
            <p className="lead" style={{ marginBottom: 8 }}>{COMPANY.blurb}</p>
            <p style={{ color: "var(--muted)" }}>
              EEG has been in clinical use for over 70 years, yet it shows only crude waveforms. Those
              waveforms are the tip of an iceberg. DECI builds on EEG to reveal the deeper, more
              meaningful electrical events inside the brain.
            </p>
            <div className="stat-row">
              <div className="stat"><b>2000+</b><span>images per second</span></div>
              <div className="stat"><b>3D</b><span>real-time localization</span></div>
              <div className="stat"><b>95%</b><span>pilot study accuracy</span></div>
            </div>
          </div>
          <div className="split-media">
            <div className="media-card"><img src="/brand/brain-4.jpg" alt="DECI brain render" /></div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section">
        <div className="wrap">
          <span className="eyebrow">What we build</span>
          <h2 className="h-sec" style={{ color: "#fff" }}>Two platforms, one mission</h2>
          <div className="prod-grid">
            <div className="prod-card" data-testid="prod-deci">
              <img className="bg" src="/brand/brain-5.jpg" alt="" />
              <h3>DECI</h3>
              <p>Dynamic Electrical Cortical Imaging: a safe, non-invasive, portable alternative to fMRI, MRI and PET that renders cortical activity as 3D motion pictures.</p>
              <Link to="/deci" className="prod-link">Learn about DECI</Link>
            </div>
            <div className="prod-card" data-testid="prod-tactile">
              <img className="bg" src="/brand/brain-2.jpg" alt="" />
              <h3>Tactile Health</h3>
              <p>An AI and medical-device venture focused on pain and touch diagnostics, and developers of the patented Woodpecker robotic testing device for Fibromyalgia and Neuropathy.</p>
              <Link to="/tactile-health" className="prod-link">Learn about Tactile Health</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PEOPLE */}
      <section className="section soft">
        <div className="wrap">
          <span className="eyebrow">Key people</span>
          <h2 className="h-sec">The team behind the science</h2>
          <div className="people-grid">
            {PEOPLE.map((p) => (
              <div className="person" key={p.name}>
                <h3>{p.name}</h3>
                <span className="role">{p.role}</span>
                <p>{p.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section">
        <div className="wrap">
          <span className="eyebrow">Research</span>
          <h2 className="h-sec" style={{ color: "#fff" }}>Projects and collaborations</h2>
          <div className="proj-list">
            {PROJECTS.map((p) => (
              <div className="proj-item" key={p} style={{ color: "#000" }}><span className="dot" /> {p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS PREVIEW */}
      {news.length > 0 && (
        <section className="section soft">
          <div className="wrap">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
              <div>
                <span className="eyebrow">Newsroom</span>
                <h2 className="h-sec">Latest news</h2>
              </div>
              <Link to="/news" className="btn btn-outline">All news</Link>
            </div>
            <div className="news-grid" style={{ marginTop: 40 }}>
              {news.map((n) => (
                <div className="news-card" key={n.id}>
                  <span className="news-date">{n.date}</span>
                  <h3>{n.title}</h3>
                  <p>{n.summary}</p>
                  <Link to={`/news/${n.id}`} className="news-more">Read more</Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT CTA */}
      <section className="section">
        <div className="wrap">
          <div className="cta-strip">
            <h2>Interested in what we are building?</h2>
            <p>Review our products, visit our Toronto showroom, or explore partnership and investment opportunities.</p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/contact" className="btn btn-primary">Contact us</Link>
              <Link to="/investor-relations" className="btn btn-ghost">Investor package</Link>
            </div>
            <div className="cta-contact">
              <span>{COMPANY.address}</span>
              <span>{COMPANY.phone}</span>
              <span>{COMPANY.email}</span>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
